import { useEffect, useState } from "react";
import { DatePill } from "../../../shared/components/DatePill/DatePill";
import type { FeedbackReviewFiltersState } from "../../hooks/useFeedbackReviewFilters";
import styles from "./FeedbackFilters.module.css";

const JURISDICTION_OPTIONS = [
  { value: "", label: "All jurisdictions" },
  { value: "nz", label: "New Zealand" },
  { value: "au-federal", label: "AU Federal" },
  { value: "au-nsw", label: "AU NSW" },
  { value: "au-vic", label: "AU VIC" },
  { value: "au-qld", label: "AU QLD" },
];

export interface FeedbackFiltersProps {
  filters: FeedbackReviewFiltersState;
}

export function FeedbackFilters({ filters }: FeedbackFiltersProps) {
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(filters.searchText);

  useEffect(() => {
    setSearchDraft(filters.searchText);
  }, [filters.searchText]);

  function handleSearchSubmit(event: React.FormEvent) {
    event.preventDefault();
    filters.setSearchText(searchDraft.trim());
  }

  return (
    <div className={styles.toolbar} data-testid="feedback-filters">
      <DatePill
        dateFrom={filters.dateRange.fromDate}
        dateTo={filters.dateRange.toDate}
        isOpen={isDatePickerOpen}
        onOpenChange={setDatePickerOpen}
        onChange={(from, to) => filters.dateRange.setDateRange(from, to)}
      />

      <label className={styles.field}>
        <span className={styles.label}>Jurisdiction</span>
        <select
          className={styles.select}
          value={filters.jurisdiction}
          onChange={(event) => filters.setJurisdiction(event.target.value)}
          aria-label="Jurisdiction filter"
        >
          {JURISDICTION_OPTIONS.map((option) => (
            <option key={option.value || "all"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <form className={styles.searchForm} onSubmit={handleSearchSubmit}>
        <label className={styles.field}>
          <span className={styles.label}>Question search</span>
          <input
            type="search"
            className={styles.input}
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Search question text"
            aria-label="Search question text"
          />
        </label>
        <button type="submit" className={styles.searchButton}>
          Search
        </button>
      </form>

      <label className={styles.field}>
        <span className={styles.label}>Workspace ID</span>
        <input
          type="text"
          className={styles.input}
          value={filters.workspaceId}
          onChange={(event) => filters.setWorkspaceId(event.target.value.trim())}
          placeholder="Optional"
          aria-label="Workspace ID filter"
        />
      </label>

      <label className={styles.field}>
        <span className={styles.label}>Org ID</span>
        <input
          type="text"
          className={styles.input}
          value={filters.orgId}
          onChange={(event) => filters.setOrgId(event.target.value.trim())}
          placeholder="Optional"
          aria-label="Org ID filter"
        />
      </label>

      {filters.hasOptionalFilters && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={() => {
            setSearchDraft("");
            filters.clearOptionalFilters();
          }}
          data-testid="clear-filters-button"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
